import axiosWrapper from './axiosWrapper';

/**
 * API工厂 - 用于创建类型安全的API调用函数
 * 
 * @template TParams 请求参数类型
 * @template TResponse 响应数据类型
 * @param url API路径
 * @param method HTTP方法
 * @param errorMessage 错误信息
 * @returns 返回一个类型安全的API调用函数
 * 
 * @example
 * // 创建一个POST请求
 * const createUser = createApiMethod<CreateUserParams, UserResponse>(
 *   '/api/users',
 *   'POST',
 *   '创建用户失败'
 * );
 * 
 * // 使用创建的API方法
 * const user = await createUser({ name: '谢鸿翔', age: 30 });
 */
export function createApiMethod<TParams extends Record<string, unknown> = Record<string, unknown>, TResponse = unknown>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  errorMessage = 'Request failed, Please try again later.'
) {
  return async (params?: TParams): Promise<TResponse> => {
    try {
      // 根据HTTP方法决定参数放在data还是params中
      const response = await axiosWrapper.request(
        method === 'GET'
          ? { url, method, params }
          : { url, method, data: params }
      );
      
      // 将响应转换为期望的类型
      return response as unknown as TResponse;
    } catch (error) {
      console.error(`${errorMessage}:`, error);
      throw error;
    }
  };
}

/**
 * 创建API模块 - 用于组织相关的API调用
 * 
 * @example
 * // 创建用户API模块
 * export const userApi = createApiModule('/api/users', {
 *   getAll: { method: 'GET' },
 *   getById: { method: 'GET', urlSuffix: '/:id' },
 *   create: { method: 'POST' },
 *   update: { method: 'PUT', urlSuffix: '/:id' },
 *   delete: { method: 'DELETE', urlSuffix: '/:id' }
 * });
 * 
 * // 使用API模块
 * const users = await userApi.getAll();
 * const user = await userApi.getById({ id: 1 });
 * const newUser = await userApi.create({ name: '谢鸿翔' });
 */
export function createApiModule<
  T extends Record<string, ApiMethodConfig>,
  TMethodParams extends Record<keyof T, Record<string, unknown>> = Record<keyof T, Record<string, unknown>>,
  TMethodResponses extends Record<keyof T, unknown> = Record<keyof T, unknown>
>(
  basePath: string,
  methodsConfig: T
): {
  [K in keyof T]: ApiMethod<TMethodParams[K], TMethodResponses[K]>
} {
  const module: Record<string, unknown> = {};
  
  // 遍历配置，创建每个API方法
  for (const [methodName, config] of Object.entries(methodsConfig)) {
    const { method = 'GET', urlSuffix = '', errorMessage } = config;
    const fullUrl = `${basePath}${urlSuffix}`;
    
    module[methodName] = createApiMethod(
      fullUrl,
      method,
      errorMessage || `${methodName}请求失败`
    );
  }
  
  return module as {
    [K in keyof T]: ApiMethod<TMethodParams[K], TMethodResponses[K]>
  };
}

// 类型定义
export interface ApiMethodConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  urlSuffix?: string;
  errorMessage?: string;
}

export type ApiMethod<TParams, TResponse> = (params?: TParams) => Promise<TResponse>; 